import React, { useState, useEffect } from "react";
import { Search, Download, Eye, X } from "lucide-react";
import {
  useGetMaintenanceReportsQuery,
  useGetMaintenanceRequestQuery,
} from "../../slices/maintenanceApiSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import Select from "react-select";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const AdminMReport = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAcademy, setSelectedAcademy] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [modalData, setModalData] = useState(null);
  const rowsPerPage = 10;

  const { data: apiData, isLoading } =
    useGetMaintenanceReportsQuery(selectedAcademy);
  const { data: academy } = useGetAcademyQuery();

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading reports, please wait...");
    } else {
      toast.dismiss();
    }
    return () => toast.dismiss();
  }, [isLoading]);

  const processApiData = (rawData) => {
    if (!rawData) return [];
    const dataArray = Array.isArray(rawData) ? [...rawData] : [rawData];

    // Sort by finishedDate/Date in desc order (newest first)
    dataArray.sort((a, b) => {
      const dateA = new Date(a.finishedDate || a.Date);
      const dateB = new Date(b.finishedDate || b.Date);
      return dateB - dateA;
    });

    return dataArray.map((item, index) => {
      return {
        rid: item.maintenanceReportID || `#${1000 + index}`,
        startTime: item.startTime || "N/A",
        endTime: item.endTime || "N/A",
        Date: item.finishedDate || new Date().toLocaleDateString(),
        Total_cost: `NU.${item.totalCost || 0}`,
        part_used: item.partsUsed || "N/A",
        total_technician: item.totalTechnicians
          ? item.totalTechnicians.toString()
          : (item.technicians ? item.technicians.split(",").length.toString() : "0"),
        Assigned_Technician: item.technicians || "N/A",
        Additional_information: item.information || "",
        imageUrl: item.images || [],
        academyName: item.academyName || "Unknown Academy",
        assetName: item.assetName || "N/A",
        assetArea: item.area || "N/A",
        original: item,
      };
    });
  };

  const [data, setData] = useState([]);

  useEffect(() => {
    if (apiData) {
      setData(processApiData(apiData));
    }
  }, [apiData]);

  const filteredData = data.filter((item) => {
    return Object.values(item).some(
      (value) =>
        value &&
        typeof value === "string" &&
        value.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  });

  const uniqueAcademies = React.useMemo(() => {
    if (!academy || !Array.isArray(academy) || academy.length === 0) {
      return [{ value: "", label: "All Academies" }];
    }

    return [
      { value: "", label: "All Academies" },
      ...academy.map((acad) => ({
        value: acad.academyId || acad._id,
        label: acad.name || "Unnamed Academy",
      })),
    ];
  }, [academy]);

  const selectedOption = React.useMemo(() => {
    return uniqueAcademies.find((opt) => opt.value === selectedAcademy) || uniqueAcademies[0];
  }, [uniqueAcademies, selectedAcademy]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleSelectRow = (rid) => {
    setSelectedRows((prev) =>
      prev.includes(rid) ? prev.filter((item) => item !== rid) : [...prev, rid],
    );
  };

  const handleDownloadSelected = () => {
    if (selectedRows.length === 0) {
      toast.error("Please select at least one report to download");
      return;
    }

    const selectedData = data.filter((item) => selectedRows.includes(item.rid));

    const csvContent = [
      [
        "Report ID",
        "Maintenance ID",
        "Start Time",
        "End Time",
        "Finished Date",
        "Total Cost",
        "Parts Used",
        "Technicians",
        "Information",
      ],
      ...selectedData.map((item) => [
        item.rid || "",
        item.original?.preventiveMaintenanceID || "",
        item.startTime || "",
        item.endTime || "",
        item.Date || "",
        item.Total_cost || "",
        item.part_used || "",
        item.Assigned_Technician || "",
        item.Additional_information || "",
      ]),
    ]
      .map((row) =>
        row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "maintenance_reports.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Reports downloaded successfully");
  };

  const handleDownloadPDF = () => {
    if (!modalData) return;

    const doc = new jsPDF();
    doc.text("Maintenance Report", 14, 15);

    const columns = ["Field", "Value"];
    const rows = [
      ["Report ID", modalData.rid || "N/A"],
      ["Maintenance ID", modalData.original?.preventiveMaintenanceID || "N/A"],
      ["Start Time", modalData.startTime || "N/A"],
      ["End Time", modalData.endTime || "N/A"],
      ["Finished Date", formatDate(modalData.Date) || "N/A"],
      ["Total Cost", modalData.Total_cost || "N/A"],
      ["Parts Used", modalData.part_used || "N/A"],
      ["Technicians", modalData.Assigned_Technician || "N/A"],
      ["Additional Information", modalData.Additional_information || "N/A"],
    ];

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 20,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`Maintenance_Report_${modalData.rid}.pdf`);
    toast.success("PDF downloaded successfully");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card px-6 py-4">
        <h1 className="text-2xl font-semibold">Maintenance Report</h1>
        <p className="text-sm text-muted-foreground mt-1">
          View and analyze preventive maintenance reports and statistics
        </p>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col sm:flex-row items-center gap-4 justify-between">
          <div className="flex flex-col sm:flex-row items-center gap-4 flex-1">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search maintenance reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="w-full sm:flex-1 sm:min-w-[200px]">
              <Select
                value={selectedOption}
                onChange={(option) => {
                  setSelectedAcademy(option ? option.value : "");
                  setCurrentPage(1);
                }}
                options={uniqueAcademies}
                placeholder="Filter by Academy"
                isClearable={true}
                menuPortalTarget={document.body}
                className="react-select-container"
                classNamePrefix="react-select"
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 6,
                  colors: { ...theme.colors, primary: "#305845" },
                })}
              />
            </div>
          </div>

          <Button
            onClick={handleDownloadSelected}
            disabled={selectedRows.length === 0}
            className="w-full sm:w-auto"
            style={{
              backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
              color: "white",
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Download Selected ({selectedRows.length})
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === displayedData.length &&
                      displayedData.length > 0
                    }
                    onChange={(e) =>
                      setSelectedRows(
                        e.target.checked
                          ? displayedData.map((item) => item.rid)
                          : [],
                      )
                    }
                    className="rounded border-input"
                  />
                </TableHead>
                <TableHead className="font-semibold">Sl. No.</TableHead>
                <TableHead className="font-semibold">Report ID</TableHead>
                <TableHead className="font-semibold">Start Time</TableHead>
                <TableHead className="font-semibold">End Time</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Total Cost</TableHead>
                <TableHead className="font-semibold">Parts Used</TableHead>
                <TableHead className="font-semibold">Technicians</TableHead>
                <TableHead className="font-semibold">Information</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="h-24 text-center text-muted-foreground"
                  >
                    No maintenance reports found.
                  </TableCell>
                </TableRow>
              ) : (
                displayedData.map((item, index) => (
                  <TableRow key={item.rid}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(item.rid)}
                        onChange={() => handleSelectRow(item.rid)}
                        className="rounded border-input"
                      />
                    </TableCell>
                    <TableCell>
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell className="font-medium">{item.rid}</TableCell>
                    <TableCell>{item.startTime}</TableCell>
                    <TableCell>{item.endTime}</TableCell>
                    <TableCell>{formatDate(item.Date)}</TableCell>
                    <TableCell>{item.Total_cost}</TableCell>
                    <TableCell>{item.part_used}</TableCell>
                    <TableCell>{item.Assigned_Technician}</TableCell>
                    <TableCell className="max-w-xs truncate">
                      {item.Additional_information}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setModalData(item)}
                        style={{
                          backgroundColor:
                            "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                          color: "white",
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="border-t bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing{" "}
            {displayedData.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}{" "}
            to {Math.min(currentPage * rowsPerPage, filteredData.length)} of{" "}
            {filteredData.length} results
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <Dialog open={!!modalData} onOpenChange={() => setModalData(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Maintenance Report Details</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Report ID</Label>
                <Input value={modalData?.rid || ""} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Maintenance ID</Label>
                <Input
                  value={modalData?.original?.preventiveMaintenanceID || ""}
                  readOnly
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input value={modalData?.startTime || ""} readOnly />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input value={modalData?.endTime || ""} readOnly />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Finished Date</Label>
              <Input
                value={formatDate(modalData?.Date) || "Not completed"}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label>Parts Used</Label>
              <Input value={modalData?.part_used || ""} readOnly />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Technicians</Label>
                <Input value={modalData?.total_technician || ""} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Total Cost</Label>
                <Input value={modalData?.Total_cost || ""} readOnly />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Technicians</Label>
              <Input value={modalData?.Assigned_Technician || ""} readOnly />
            </div>

            <div className="space-y-2">
              <Label>Information</Label>
              <Textarea
                value={modalData?.Additional_information || ""}
                readOnly
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Maintenance Images</Label>
              <div className="grid grid-cols-2 gap-4">
                {Array.isArray(modalData?.imageUrl) &&
                modalData.imageUrl.length > 0 ? (
                  modalData.imageUrl.map((imgSrc, index) => (
                    <img
                      key={index}
                      src={imgSrc}
                      alt={`Work Order ${index + 1}`}
                      className="w-full h-48 object-cover rounded-md border"
                    />
                  ))
                ) : modalData?.imageUrl ? (
                  <img
                    src={modalData.imageUrl}
                    alt="Work Order"
                    className="w-full h-48 object-cover rounded-md border"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground col-span-2 text-center py-8">
                    No image available
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setModalData(null)}>
                Close
              </Button>
              <Button onClick={handleDownloadPDF}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMReport;
